/**
 * Atlas PM · 项目管理 · 项目类型数据 hook（P4.8）
 *
 * 从公开只读接口 `/api/project-types` 拉取**启用中**的项目类型，
 * 供「新建 / 编辑项目」表单使用；管理员在系统设置里改名后，刷新即生效。
 *
 * 兜底：请求失败 / 返回空数组时，回退到 `PROJECT_TYPES` 硬编码常量，
 *       保证下拉框始终有可选项。
 *
 * 兼容：拉取成功后写入 `api.ts` 的运行时缓存，使纯函数 `projectTypeLabel()`
 *       （地图筛选等旧调用点）也能拿到最新名称。
 */

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PROJECT_TYPES,
  activeProjectTypeApi,
  setRuntimeProjectTypes,
  type Option,
} from './api';

/** 缓存 key（就地定义，不改 api/queryKeys.ts，保持文件边界自包含） */
const KEY = ['project-types', 'active'] as const;

export interface UseProjectTypesResult {
  /** 类型选项：API 数据，失败时回退硬编码 */
  options: readonly Option[];
  /** 首次加载中 */
  isLoading: boolean;
  /** 请求失败（此时 options 为回退值） */
  isError: boolean;
  /** 当前是否使用了回退列表 */
  isFallback: boolean;
}

/**
 * 拉取启用中的项目类型（值 = code，标签 = name，附带业务色值）。
 *
 * 类型很少变动，staleTime 设 30 分钟，避免频繁请求。
 */
export function useProjectTypes(): UseProjectTypesResult {
  const query = useQuery({
    queryKey: KEY,
    queryFn: activeProjectTypeApi.list,
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });

  const options = useMemo<readonly Option[]>(() => {
    const list = query.data;
    if (list && list.length > 0) {
      return list.map((t) => ({ value: t.code, label: t.name, color: t.color }));
    }
    return PROJECT_TYPES;
  }, [query.data]);

  useEffect(() => {
    const list = query.data;
    setRuntimeProjectTypes(
      list && list.length > 0
        ? list.map((t) => ({ value: t.code, label: t.name, color: t.color }))
        : null,
    );
  }, [query.data]);

  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
    isFallback: !(query.data && query.data.length > 0),
  };
}
